import { Platform, StyleProp, ViewStyle } from "react-native";
import {
  AnimatedStyle,
  runOnJS,
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import { DefaultTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useCallback, useState } from "react";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";

const RESULT_ITEM_GRID_LIST_MIN_WIDTH_WEB = 240;
const RESULT_ITEM_GRID_LIST_MIN_WIDTH = 176;

export const getStickyHeaderIndices = (
  selectedViewType: SearchResultsAndFavoritesEnum,
  isTablet: boolean | undefined
) =>
  selectedViewType === SearchResultsAndFavoritesEnum.LIST && isTablet
    ? [0]
    : undefined;

export const getContainerStyle = (
  selectedViewType: SearchResultsAndFavoritesEnum,
  theme: DefaultTheme
): StyleProp<AnimatedStyle<StyleProp<ViewStyle>>> => {
  return selectedViewType === SearchResultsAndFavoritesEnum.GRID
    ? ({
        paddingBottom: Platform.OS === "ios" ? 110 : 80,
        backgroundColor: theme["core-02"],
        marginTop: theme.isMobile ? SpaceType.Basic : SpaceType.Double,
        flexGrow: 1,
      } as AnimatedStyle<ViewStyle>)
    : ({
        paddingBottom:
          Platform.OS === "ios" ? 110 : theme?.isLargeScreen ? 0 : 80,
        backgroundColor: theme["core-02"],
      } as AnimatedStyle<ViewStyle>);
};

const numOfColumns = (
  containerWidth: number,
  theme: DefaultTheme,
  isFilterVisible?: boolean
): number => {
  if (!containerWidth) return 2;
  let itemsPerRow = Math.round(
    containerWidth /
      (theme.isLargeScreen
        ? RESULT_ITEM_GRID_LIST_MIN_WIDTH_WEB
        : RESULT_ITEM_GRID_LIST_MIN_WIDTH + SpaceType.Basic)
  );
  if (theme.isTablet) {
    const itemWidth = theme.isLargeScreen
      ? RESULT_ITEM_GRID_LIST_MIN_WIDTH_WEB
      : RESULT_ITEM_GRID_LIST_MIN_WIDTH + SpaceType.Basic;

    const effectiveWidth = isFilterVisible
      ? containerWidth * 0.9
      : containerWidth * 0.9;

    itemsPerRow = Math.floor(effectiveWidth / itemWidth);
  }
  if (itemsPerRow > 6) itemsPerRow = 6;
  return itemsPerRow;
};
export const getNumOfColumns = (
  selectedViewType: SearchResultsAndFavoritesEnum,
  containerWidth: number,
  theme: DefaultTheme,
  isFilterVisible?: boolean
) =>
  selectedViewType === SearchResultsAndFavoritesEnum.GRID
    ? numOfColumns(containerWidth, theme, isFilterVisible)
    : 1;

export const isPresentNumOfColumnsFull = (
  productListViewData: any,
  defaultListDataOptions: any,
  customizeColumnParams: any,
  containerWidth: number
) => {
  const listDataOptions =
    customizeColumnParams?.length > 0
      ? customizeColumnParams
      : productListViewData?.length > 0
      ? productListViewData
      : defaultListDataOptions;

  const filteredListDataOptions = listDataOptions.filter(
    (item) => item?.is_visible !== false
  );

  const default_width = filteredListDataOptions
    .map((item) => parseInt(item.deafult_width, 10))
    .reduce((sum, width) => sum + width, 0);

  return containerWidth - 100 < default_width;
};

export const getVisibleItems = (productListViewData) => {
  return productListViewData
    .filter((item) => item.is_visible === false)
    .reduce((acc, item) => {
      acc[item.name] = true;
      return acc;
    }, {});
};

export const getToastBottomMargin = (scrollValue: any, bottom: any) => {
  if (Platform.OS === "web") return 110;

  return scrollValue.value >= 1
    ? Math.min(70, bottom + 50)
    : Math.min(110, bottom + 80);
};

export const mergeDiamondsAndPromotionsPerPage = (pages: any) => {
  return pages.flatMap((page: any) => {
    const body = page?.data?.response?.body;

    if (!body) {
      return [];
    }

    const { diamonds, gemstones, jewelries, promotions } = body;
    const result = [...(diamonds || gemstones || jewelries || [])];

    const sortedPromotions = [...(promotions || [])].sort(
      (a, b) => b.promotion_index - a.promotion_index
    );

    for (const promotion of sortedPromotions) {
      if (
        promotion.promotion_index >= 0 &&
        promotion.promotion_index <= result.length
      ) {
        result.splice(promotion.promotion_index, 0, promotion);
      }
    }

    return result;
  });
};

// Do not react to UpdatedValue by writing it back — that recurses in
// Reanimated's mapper registry and SIGSEGVs inside libreactnative.so.
export const useCustomScrollHandler = (xOffset) => {
  const UpdatedValue = useSharedValue(xOffset?.value ?? 0);
  const lastReportedX = useSharedValue(-1);
  const lastAtRightEnd = useSharedValue(false);
  const [scrollValue, setScrollValue] = useState(0);
  const [isAtRightEnd, setIsAtRightEnd] = useState(false);

  const updateScrollUi = useCallback((x: number, atRightEnd: boolean) => {
    setScrollValue(x);
    setIsAtRightEnd(atRightEnd);
  }, []);

  const scrollHandler = useAnimatedScrollHandler((event) => {
    const x = event.contentOffset.x;
    UpdatedValue.value = x;
    xOffset.value = Math.max(x, 0);
    const atRightEnd =
      x + event.layoutMeasurement.width >= event.contentSize.width - 1;

    if (
      Math.abs(x - lastReportedX.value) >= 8 ||
      atRightEnd !== lastAtRightEnd.value
    ) {
      lastReportedX.value = x;
      lastAtRightEnd.value = atRightEnd;
      runOnJS(updateScrollUi)(x, atRightEnd);
    }
  });

  return {
    scrollHandler,
    UpdatedValue,
    scrollValue,
    isAtRightEnd,
  };
};
